// =============================================================================
// ui/tabs/LoggerDownload.tsx — My Account → Download the indicator.
// -----------------------------------------------------------------------------
// Pairs with Realtime Sync: get the key there, get the file here.
//
// ── ⚠ THE INSTALL STEPS ARE TRANSCRIBED WORD FOR WORD, AND THAT IS DELIBERATE ──
//
// They are not generic instructions. Each one encodes something that was learned
// by running these loggers on real trades:
//
//   • ONE LOGGER PER INSTRUMENT on ATAS — the screenshot and MAE/MFE only cover
//     that chart's instrument, so a logger on ES will not sync NQ. Two on the
//     same instrument race for the screenshot.
//   • PER CHART on Sierra, not per platform.
//   • SIERRA HAS NO LOCAL FALLBACK — a push that fails after retries is lost.
//
// Someone rewording these to be "clearer" removes the reason they exist. If they
// change, they change in the old client first and get copied.
//
// ── ⚠ THE DOWNLOAD IS A PLAIN LINK, NOT A FETCH ──
//
// A `<a download>` on a cross-origin URL has its filename hint IGNORED by every
// browser, so the name comes from the object path — which is why the manifest
// stores a path ending in the real `.dll`. Fetching it into a blob to force the
// name would pull a megabyte through JS for no gain and break on a slow link.
// =============================================================================

import { useEffect, useState } from 'react';
import { ENV } from '../../env';
import { loadLoggerManifest, type ManifestResult } from '../../io/storage/loggerManifest';
import {
  LOGGER_PLATFORMS, downloadUrl, formatSize, loggerBucketBase,
  type LoggerBuild,
} from '../../selectors/loggerManifest';

export function LoggerDownloadCard() {
  const base = loggerBucketBase(ENV.config.supabaseUrl);
  const [result, setResult] = useState<ManifestResult | null>(null);
  const [openSteps, setOpenSteps] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const r = await loadLoggerManifest(base);
      if (!cancelled) setResult(r);
    })();
    return () => { cancelled = true; };
  }, [base]);

  return (
    <section className="card" data-testid="logger-download">
      <h3>Download the indicator</h3>
      <p className="muted">
        Install the logger for your platform, then paste the key above into its{' '}
        <b>Cloud Sync</b> settings. Beta builds are unsigned — Windows may warn on
        first run; that is expected.
      </p>

      {result === null ? (
        <p className="muted lg-note">Checking for the latest build…</p>
      ) : result.status === 'unreachable' ? (
        // ⚠ NOT "coming soon". The old client shows one message for both, and
        // they call for opposite actions — this one means check your connection,
        // and the build may well already exist.
        <p className="muted lg-note" role="alert">
          Couldn’t reach the download server ({result.error}). Your key and
          endpoint above are unaffected — try again in a moment.
        </p>
      ) : result.status === 'unpublished' ? (
        <p className="muted lg-note">
          Downloads are being prepared — check back shortly. Your key and endpoint
          above are ready to go.
        </p>
      ) : (
        <div className="lg-list">
          {LOGGER_PLATFORMS.map((p) => (
            <PlatformRow
              key={p.key}
              label={p.label}
              build={result.manifest[p.key] ?? null}
              base={base}
              stepsOpen={openSteps === p.key}
              onToggleSteps={() => setOpenSteps(openSteps === p.key ? null : p.key)}
              platform={p.key}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function PlatformRow(
  { label, build, base, stepsOpen, onToggleSteps, platform }: {
    label: string;
    build: LoggerBuild | null;
    base: string;
    stepsOpen: boolean;
    onToggleSteps: () => void;
    platform: string;
  },
) {
  // ⚠ A platform with no usable entry says "Coming soon" rather than vanishing.
  // A card listing one platform reads as though the other is unsupported.
  if (!build) {
    return (
      <div className="lg-row">
        <div className="lg-head">
          <div className="grow"><b>{label}</b></div>
          <span className="muted">Coming soon</span>
        </div>
      </div>
    );
  }

  return (
    <div className="lg-row">
      <div className="lg-head">
        <div className="grow">
          <b>{label}</b>
          <span className="muted lg-ver"> · v{build.version} · {formatSize(build.size)}</span>
        </div>
        <a className="btn primary lg-dl" href={downloadUrl(base, build)} download>
          ⤓ Download
        </a>
        <button type="button" className="btn lg-steps-btn"
          aria-expanded={stepsOpen} onClick={onToggleSteps}>
          Install steps
        </button>
      </div>
      {stepsOpen ? <InstallSteps platform={platform} /> : null}
    </div>
  );
}

/**
 * ⚠ TRANSCRIBED. See the header — every clause is load-bearing.
 */
function InstallSteps({ platform }: { platform: string }) {
  if (platform === 'sierra') {
    return (
      <ol className="muted lg-steps">
        <li>
          Copy <code>JtfTradeSync_64.dll</code> into your Sierra Chart <b>Data</b>{' '}
          folder, then restart Sierra Chart.
        </li>
        <li>
          Add the <b>JTF Trade Sync</b> study to <b>each chart you trade</b> — it
          is per-chart.
        </li>
        <li>
          Open the study settings, paste your <b>Ingest API Key</b> from above
          (sync is on by default), and leave the chart running.
        </li>
        <li>
          Note: Sierra has no local fallback — a push that fails after retries is
          lost, so keep the platform online.
        </li>
      </ol>
    );
  }

  return (
    <ol className="muted lg-steps">
      <li>
        Drop <code>JtfTradeLogger.dll</code> into{' '}
        <code>%APPDATA%\ATAS\Indicators</code>, then restart ATAS.
      </li>
      <li>
        Add the <b>JTF Trade Logger</b> indicator to{' '}
        <b>each instrument you trade</b> — one on your ES chart, one on your NQ
        chart, and so on. Cloud sync, the chart screenshot and MAE/MFE only cover{' '}
        <b>that chart’s instrument</b>, so a logger on ES will not sync your NQ
        trades. Put it on the chart you actually want captured (your execution
        chart) — it screenshots that chart even when minimized or behind other
        windows.
      </li>
      <li>
        In its settings open <b>Cloud Sync</b>: turn <b>Enable Cloud Push ON</b>{' '}
        (off by default), paste your <b>Ingest API Key</b> from above, and confirm
        the endpoint URL matches.
      </li>
      <li>
        Take a trade — completed round-trips land in TRADECOACH within seconds of
        going flat.
      </li>
      <li>
        Note: the indicator only runs while its chart is open, and{' '}
        <b>one logger per instrument</b> — two on the same instrument race for the
        screenshot and TRADECOACH will warn you.
      </li>
    </ol>
  );
}
